import { yupResolver } from '@hookform/resolvers/yup';
import * as M from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useGetAiAgentServiceOperationSettings } from '../../../../../../shared/apis/ai-agent/company/reactQueries';
import { mgptServiceChannelOptions } from '../../../../../../shared/constants/aiAgentOptions';
import { queries } from '../../../../../../shared/queries';
import { filterAiAgentTargetServices } from '../../../../../../shared/utils/aiAgentServiceChannel';
import { serviceOperationSettingsDefaultValues, serviceOperationSettingsSchema } from '../constants/serviceOperationSettings';
import usePutServiceOptions from '../hooks/usePutServiceOptions';

import type { IAiAgentSupportedLanguageOption } from '../../../../../../shared/apis/ai-agent/company/types';
import type { ServiceOperationSettingsFormValues } from '../constants/serviceOperationSettings';

interface ServiceOperationSettingsModalProps {
  opened: boolean;
  onClose: () => void;

  selectedCompanyId: number;
}

export default function ServiceOperationSettingsModal(props: ServiceOperationSettingsModalProps) {
  const { opened, onClose, selectedCompanyId } = props;
  const queryClient = useQueryClient();
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ServiceOperationSettingsFormValues>({
    resolver: yupResolver(serviceOperationSettingsSchema),
    defaultValues: serviceOperationSettingsDefaultValues,
  });

  const { data: serviceOperationSettings } = useGetAiAgentServiceOperationSettings(selectedCompanyId);

  const { mutate: putServiceOptions, isLoading } = usePutServiceOptions();

  const languageOptions: IAiAgentSupportedLanguageOption[] = JSON.parse(
    serviceOperationSettings?.candidatedLanguages || '[]',
  ).map((lang: IAiAgentSupportedLanguageOption) => ({
    value: lang.value,
    label: lang.label,
  }));

  const handleClose = () => {
    reset(serviceOperationSettingsDefaultValues);
    onClose();
  };

  const handleSubmitSettings = (values: ServiceOperationSettingsFormValues) => {
    const supportedLanguages = languageOptions.filter((option) => (values.supportedLanguages ?? []).includes(option.value));

    putServiceOptions(
      {
        companyId: selectedCompanyId,
        data: {
          feedbackManageYn: values.feedbackManageYn,
          dictionaryLimit: values.dictionaryLimit,
          pageLimit: values.pageLimit,
          accountLimit: values.accountLimit,
          companyChatTurnLimitMonthly: values.companyChatTurnLimitMonthly,
          companyChatTurnLimitDaily: values.companyChatTurnLimitDaily,
          userChatTurnLimitDaily: values.userChatTurnLimitDaily,
          supportedLanguages,
          memo: values.memo,
        },
      },
      {
        onSuccess: () => {
          handleClose();
          queryClient.invalidateQueries({ queryKey: queries.aiAgent.aiAgentCompanies._def });
        },
      },
    );
  };

  useEffect(() => {
    if (serviceOperationSettings) {
      const parsedLanguages = JSON.parse(serviceOperationSettings.supportedLanguages || '[]');
      reset({
        feedbackManageYn: serviceOperationSettings.feedbackManageYn,
        dictionaryLimit: serviceOperationSettings.dictionaryLimit,
        pageLimit: serviceOperationSettings.pageLimit,
        accountLimit: serviceOperationSettings.accountLimit,
        companyChatTurnLimitMonthly: serviceOperationSettings.companyChatTurnLimitMonthly,
        companyChatTurnLimitDaily: serviceOperationSettings.companyChatTurnLimitDaily,
        userChatTurnLimitDaily: serviceOperationSettings.userChatTurnLimitDaily,
        supportedLanguages: parsedLanguages.map((lang: IAiAgentSupportedLanguageOption) => lang.value),
        memo: serviceOperationSettings.memo ?? '',
      });
    }
  }, [reset, serviceOperationSettings]);

  return (
    <M.Modal title="AI-Agent 서비스 운영 설정" opened={opened} onClose={handleClose} size="md" centered>
      <form onSubmit={handleSubmit(handleSubmitSettings)} noValidate>
        <M.Text size="xs" c="secondary" mb="sm">
          *은 필수 입력 항목입니다.
        </M.Text>
        <M.Stack>
          <M.Text size="md" fw={600}>
            기본 설정
          </M.Text>
          <M.TextInput label="회사" withAsterisk readOnly disabled value={serviceOperationSettings?.companyName || '회사명'} />
          <M.MultiSelect
            label="서비스 채널"
            withAsterisk
            data={[...mgptServiceChannelOptions]}
            readOnly
            disabled
            value={filterAiAgentTargetServices(serviceOperationSettings?.channels)}
          />
          <Controller
            name="accountLimit"
            control={control}
            render={({ field }) => (
              <M.NumberInput
                label="각 서비스 계정 가입 제한 수"
                withAsterisk
                thousandSeparator=","
                min={0}
                allowDecimal={false}
                allowNegative={false}
                value={field.value}
                onChange={field.onChange}
                error={errors.accountLimit?.message}
              />
            )}
          />
          <Controller
            name="feedbackManageYn"
            control={control}
            render={({ field }) => (
              <M.Input.Wrapper label="피드백 관리" withAsterisk>
                <M.Switch
                  checked={field.value === 'Y'}
                  onChange={(event) => field.onChange(event.currentTarget.checked ? 'Y' : 'N')}
                />
              </M.Input.Wrapper>
            )}
          />
          <M.SimpleGrid cols={2} spacing="md">
            <Controller
              name="dictionaryLimit"
              control={control}
              render={({ field }) => (
                <M.NumberInput
                  label="사전 제한 수"
                  withAsterisk
                  thousandSeparator=","
                  min={0}
                  allowDecimal={false}
                  allowNegative={false}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.dictionaryLimit?.message}
                />
              )}
            />
            <Controller
              name="pageLimit"
              control={control}
              render={({ field }) => (
                <M.NumberInput
                  label="학습 페이지 제한 수"
                  withAsterisk
                  thousandSeparator=","
                  min={0}
                  allowDecimal={false}
                  allowNegative={false}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.pageLimit?.message}
                />
              )}
            />
          </M.SimpleGrid>
          <M.Divider />
          <M.Text size="xs" c="secondary">
            ※ 질답 한도에 0을 입력하면 무제한입니다.
          </M.Text>
          <M.SimpleGrid cols={2} spacing="md">
            <Controller
              name="companyChatTurnLimitMonthly"
              control={control}
              render={({ field }) => (
                <M.NumberInput
                  label="회사 월 질답 총 한도"
                  thousandSeparator=","
                  min={0}
                  allowDecimal={false}
                  allowNegative={false}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  error={errors.companyChatTurnLimitMonthly?.message}
                />
              )}
            />
            <Controller
              name="companyChatTurnLimitDaily"
              control={control}
              render={({ field }) => (
                <M.NumberInput
                  label="회사 일 질답 총 한도"
                  thousandSeparator=","
                  min={0}
                  allowDecimal={false}
                  allowNegative={false}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  error={errors.companyChatTurnLimitDaily?.message}
                />
              )}
            />
            <Controller
              name="userChatTurnLimitDaily"
              control={control}
              render={({ field }) => (
                <M.NumberInput
                  label="계정 일 질답 제한"
                  thousandSeparator=","
                  min={0}
                  allowDecimal={false}
                  allowNegative={false}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  error={errors.userChatTurnLimitDaily?.message}
                  style={{ gridColumn: 'span 2' }}
                />
              )}
            />
          </M.SimpleGrid>
          <M.Divider />
          <Controller
            name="supportedLanguages"
            control={control}
            render={({ field }) => (
              <M.MultiSelect
                label="M-GPT 지원 언어"
                withAsterisk
                data={languageOptions}
                value={languageOptions.filter((option) => (field.value ?? []).includes(option.value)).map(({ value }) => value)}
                onChange={field.onChange}
                error={errors.supportedLanguages?.message}
              />
            )}
          />
          <M.Textarea
            label="메모"
            minRows={5}
            maxRows={5}
            autosize
            maxLength={256}
            error={errors.memo?.message}
            {...register('memo')}
          />
          <M.Group justify="center" mt="md">
            <M.Button type="button" variant="outline" onClick={handleClose}>
              취소
            </M.Button>
            <M.Button type="submit" color="secondary" loading={isLoading}>
              저장
            </M.Button>
          </M.Group>
        </M.Stack>
      </form>
    </M.Modal>
  );
}
